import { useEffect, useState } from 'react';
import { Text } from '@mantine/core';
import { t } from 'i18next';
import { useRenderInContext } from '@/hooks';
import { useStore, type SuggestedChange } from '@/store';
import { asText, type ResolvedChange } from './change-cards';
import type { Blocks } from './graph-change';
import { previewTemplate } from './preview-template';
import type { Graph } from '../../../graph-editor/types';

type Preview =
  | { state: 'loading' }
  | { state: 'done'; value?: string }
  | { state: 'failed' };

type RenderedPreviewProps = {
  change: SuggestedChange;
  entryId: string;
  blocks: Blocks;
  /** The field's graph now, whose nodes a graph change may keep. */
  currentGraph?: Graph;
};

/**
 * What the change gives the field for this document: the field's new
 * template, rendered in its place in the template, the way the AI suggestions
 * are checked.
 */
const RenderedPreview = ({
  change,
  entryId,
  blocks,
  currentGraph,
}: RenderedPreviewProps) => {
  const renderInContext = useRenderInContext();
  const getKey = useStore(({ entries: e }) => e.getKey);
  const getGraphTemplate = useStore(({ graphs: g }) => g.getGraphTemplate);
  const [preview, setPreview] = useState<Preview>({ state: 'loading' });
  const changeKey = JSON.stringify(change);

  useEffect(() => {
    let current = true;
    const show = (next: Preview) => current && setPreview(next);

    const template = previewTemplate(
      change,
      entryId,
      getKey(entryId),
      blocks,
      getGraphTemplate,
      currentGraph,
    );
    if (template === undefined) {
      show({ state: 'failed' });
    } else {
      renderInContext(entryId)(JSON.stringify(template))
        .then((result) =>
          show(
            result.isOk()
              ? { state: 'done', value: asText(result.value) }
              : { state: 'failed' },
          ),
        )
        .catch(() => show({ state: 'failed' }));
    }
    return () => {
      current = false;
    };
    // Once per change: the render function is a new one on every render.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [changeKey, entryId]);

  if (preview.state === 'done') {
    return (
      <Text fz="xs" mt={4}>
        {preview.value === undefined
          ? t('chat.change.preview.empty')
          : t('chat.change.preview.gives', { value: preview.value })}
      </Text>
    );
  }
  return (
    <Text fz="xs" c="dimmed" mt={4}>
      {t(
        preview.state === 'loading'
          ? 'chat.change.preview.loading'
          : 'chat.change.preview.failed',
      )}
    </Text>
  );
};

type ChangePreviewProps = {
  change: SuggestedChange;
  resolved: ResolvedChange;
  applied: boolean;
  blocks: Blocks;
  currentGraph?: Graph;
};

/**
 * What a column or graph change gives the field, before it is applied. A value
 * or an AI suggestion shows what it gives already. There is nothing to render
 * before a template and its document are loaded.
 */
export const ChangePreview = ({
  change,
  resolved,
  applied,
  blocks,
  currentGraph,
}: ChangePreviewProps) => {
  const ready = useStore(({ config: c }) =>
    Boolean(c.templateVariant && c.textContent),
  );
  const { entryId, problem } = resolved;
  if (!ready || applied || problem || !entryId) return null;
  if (change.kind !== 'column' && change.kind !== 'graph') return null;
  return (
    <RenderedPreview
      change={change}
      entryId={entryId}
      blocks={blocks}
      currentGraph={currentGraph}
    />
  );
};
